import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { trackLanguageChange, trackFileDownload, trackButtonClick, trackVideoInteraction } from '../utils/analytics';
import { useTranslation } from '../hooks/useTranslation';

export default function LanguageSwitch() {
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const playerRef = useRef(null);
  const youtubeIframeRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cvFileName = language === 'pt' ? 'CV_PT_Daniel_Marnet.pdf' : 'CV_EN_Daniel_Marnet.pdf';
  const cvTitle = language === 'pt' ? 'PDF Curriculum PT' : 'PDF Curriculum EN';
  const whatsappMessage = language === 'pt' ? 'Ol%C3%A1!' : 'Hello%21';
  const whatsappUrl = `https://wa.me/5512991876096?text=${whatsappMessage}`;
  const buttonStyle = {
    background: 'rgba(0, 0, 0, 0.7)',
    border: '2px solid var(--accent)',
    borderRadius: '2rem',
    padding: '0.5rem 1rem',
    color: 'var(--text-light)',
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    minWidth: '70px',
    width: '70px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 15px rgba(0, 245, 255, 0.3)',
    transition: 'all 0.3s ease',
    textDecoration: 'none'
  };

  const handleMouseEnter = (e) => {
    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 245, 255, 0.5)';
    e.currentTarget.style.borderColor = 'rgba(0, 245, 255, 1)';
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 245, 255, 0.3)';
    e.currentTarget.style.borderColor = 'var(--accent)';
  };

  // YouTube IFrame API
  useEffect(() => {
    // Carrega o script do YouTube IFrame API
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    window.onYouTubeIframeAPIReady = () => {
      if (!playerRef.current && youtubeIframeRef.current) {
        playerRef.current = new window.YT.Player(youtubeIframeRef.current, {
          videoId: 'pk5CwmceVHs',
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            fs: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            showinfo: 0,
            volume: 40
          },
          events: {
            onReady: (event) => {
              event.target.setVolume(40);
            }
          }
        });
      }
    };

    // Se a API já estiver carregada
    if (window.YT && window.YT.Player && !playerRef.current) {
      window.onYouTubeIframeAPIReady();
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          // Erro silencioso
        }
      }
    };
  }, []);

  const toggleMusic = () => {
    if (!playerRef.current) return;

    try {
      if (isPlaying) {
        // Pausar
        playerRef.current.pauseVideo();
        setIsPlaying(false);
        trackVideoInteraction('pause', 'Background Music');
      } else {
        // Tocar
        playerRef.current.playVideo();
        playerRef.current.setVolume(40);
        playerRef.current.unMute();
        setIsPlaying(true);
        trackVideoInteraction('play', 'Background Music');
      }
    } catch (e) {
      // Erro silencioso
    }
  };

  return (
    <>
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 999,
              backdropFilter: 'blur(2px)'
            }}
          />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          alignItems: 'flex-end'
        }}
      >
        <AnimatePresence>
          {(isOpen || !isMobile) && (
            <motion.div
              key="mobile-menu-items"
              initial={isMobile ? { opacity: 0, y: 20, scale: 0.8 } : { opacity: 1, scale: 1 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={isMobile ? { opacity: 0, y: 20, scale: 0.8 } : { opacity: 1, scale: 1 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                alignItems: 'flex-end'
              }}
            >
              {/* Botão de Tradução */}
              <motion.button
                onClick={() => {
                  const fromLang = language;
                  const toLang = language === 'pt' ? 'en' : 'pt';
                  trackLanguageChange(fromLang, toLang);
                  toggleLanguage();
                  if (isMobile) setIsOpen(false);
                }}
                title={language === 'pt' ? 'Português' : 'English'}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={buttonStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <i className="bi bi-translate" style={{ fontSize: '1.2rem' }} aria-label="Traduzir" />
                <span>{language === 'pt' ? 'PT' : 'EN'}</span>
              </motion.button>

              {/* Botão CV */}
              <motion.a
                href={`/${cvFileName}`}
                target="_blank"
                rel="noopener noreferrer"
                title={cvTitle}
                onClick={() => {
                  trackFileDownload(cvFileName, 'pdf');
                  if (isMobile) setIsOpen(false);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={buttonStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <i className="bi bi-file-earmark-pdf" style={{ fontSize: '1.2rem' }} aria-label="Curriculum PDF" />
                <span>CV</span>
              </motion.a>

              {/* Botão WhatsApp */}
              <motion.a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                onClick={() => {
                  trackButtonClick('WhatsApp', 'floating-buttons');
                  if (isMobile) setIsOpen(false);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={buttonStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <i className="bi bi-whatsapp" style={{ fontSize: '1.2rem' }} aria-label="WhatsApp" />
                <span>WA</span>
              </motion.a>

              {/* Botão Música */}
              <motion.button
                onClick={() => {
                  toggleMusic();
                  // No mobile, talvez seja melhor não fechar ao clicar na música para o usuário ajustar volume se tivesse
                }}
                title={t('music.tooltip')}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                animate={isPlaying ? {
                  y: [0, -3, 0],
                  rotate: [0, 5, 0, -5, 0]
                } : {}}
                transition={isPlaying ? {
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                } : {}}
                style={buttonStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <i className={`bi bi-${isPlaying ? 'music-note-list' : 'music-note'}`} style={{ fontSize: '1.2rem' }} aria-label="Música" />
                <span>MU</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Botão Principal Toggle (apenas Mobile) */}
        {isMobile && (
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              ...buttonStyle,
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'var(--accent)',
              color: '#000',
              border: 'none',
              fontSize: '1.5rem',
              boxShadow: '0 8px 25px rgba(0, 245, 255, 0.5)'
            }}
          >
            <i className={`bi bi-${isOpen ? 'x-lg' : 'grid-fill'}`}></i>
          </motion.button>
        )}

        {/* YouTube Player oculto */}
        <div style={{ display: 'none' }}>
          <div ref={youtubeIframeRef} id="youtube-player"></div>
        </div>
      </motion.div>
    </>
  );
}






